import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, ToggleButton, ToggleButtonGroup, Typography, Chip, Alert, CircularProgress, TextField, Tooltip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';

import { uptimeAPI } from '../services/api';
import { MONTHS, pct } from './UptimeDetailDialog';
import { saveBlob } from '../utils/download';

const key = (y, m) => `${y}-${String(m).padStart(2, '0')}`;
const monthsAgo = (n) => { const d = new Date(); d.setUTCDate(1); d.setUTCMonth(d.getUTCMonth() - n); return key(d.getUTCFullYear(), d.getUTCMonth() + 1); };
const PERIODS = [3, 6, 12, 24, 36];
const label = (y, m) => `${MONTHS[m - 1].slice(0, 3)} ${y}`;
const csvCell = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;

// The average uptime of all customers for each month of a chosen period, with a CSV of the same figures per customer
function UptimeAverageDialog({ onClose }) {
  const [from, setFrom] = useState(monthsAgo(11));
  const [to, setTo] = useState(monthsAgo(0));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const preset = PERIODS.find((n) => to === monthsAgo(0) && from === monthsAgo(n - 1)) || null;
  const [error, setError] = useState(null);

  useEffect(() => {
    let live = true;
    const [fy, fm] = from.split('-').map(Number);
    const [ty, tm] = to.split('-').map(Number);
    setLoading(true); setError(null);
    uptimeAPI.averages(fy, fm, ty, tm)
      .then((r) => { if (live) setData(r.data); })
      .catch((err) => { if (live) { setData(null); setError(err.response?.data?.detail || 'Could not work out the averages'); } })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [from, to]);

  const exportCsv = () => {
    const cols = data.months.map((m) => key(m.year, m.month));
    const head = ['Customer code', 'Customer', ...cols.map((c) => (c === key(data.last.year, data.last.month) && !data.months[data.months.length - 1].complete ? `${c} (so far)` : c)), 'Period average'];
    const avg = ['', 'Average of all customers', ...data.months.map((m) => m.average_percent), data.period_average_percent];
    const lines = data.customers.map((c) => [c.code, c.name, ...cols.map((k) => c.months[k]), c.average_percent]);
    const csv = [head, avg, ...lines].map((r) => r.map(csvCell).join(',')).join('\r\n');
    saveBlob(new Blob([csv], { type: 'text/csv' }), `average-uptime-${from}-to-${to}.csv`);
  };

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Average uptime of all customers</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mt: 1, mb: 2 }}>
          <ToggleButtonGroup size="small" exclusive value={preset} aria-label="Period" onChange={(e, n) => { if (n) { setFrom(monthsAgo(n - 1)); setTo(monthsAgo(0)); } }}>
            {PERIODS.map((n) => <ToggleButton key={n} value={n} aria-label={`Last ${n} months`}>{n} months</ToggleButton>)}
          </ToggleButtonGroup>
          <TextField size="small" type="month" label="From" value={from} onChange={(e) => e.target.value && setFrom(e.target.value)} slotProps={{ inputLabel: { shrink: true } }} />
          <TextField size="small" type="month" label="To" value={to} onChange={(e) => e.target.value && setTo(e.target.value)} slotProps={{ inputLabel: { shrink: true } }} />
        </Box>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
          Each month's average is the mean of the customers' uptime for that month, so every customer counts once. Customers with no results in a month are left out of it. Planned maintenance is already taken off, as on the monthly screen.
        </Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {loading && <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>}
        {!loading && data && (
          <>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 2 }}>
              <Typography variant="h4" component="span" sx={{ fontWeight: 'bold' }}>{pct(data.period_average_percent)}</Typography>
              <Typography variant="body2" color="text.secondary">average over {data.months_with_data} month{data.months_with_data === 1 ? '' : 's'} with data (target {data.target_percent}%)</Typography>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Month</TableCell><TableCell align="right">Customers</TableCell><TableCell align="right">Average uptime</TableCell>
                    <TableCell align="right"><Tooltip title="The same month worked out over all the minutes observed, so a larger system counts for more. A cross-check on the average."><span>All minutes</span></Tooltip></TableCell>
                    <TableCell align="right">Below target</TableCell><TableCell>Lowest customer</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.months.map((m) => (
                    <TableRow key={key(m.year, m.month)} hover>
                      <TableCell>{label(m.year, m.month)}{!m.complete && <Chip size="small" variant="outlined" label="so far" sx={{ ml: 1 }} />}</TableCell>
                      <TableCell align="right">{m.customers || '-'}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>{pct(m.average_percent)}</TableCell>
                      <TableCell align="right">{pct(m.pooled_percent)}</TableCell>
                      <TableCell align="right">{m.customers ? m.below_target : '-'}</TableCell>
                      <TableCell>{m.lowest ? `${m.lowest.code} ${pct(m.lowest.uptime_percent)}` : '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button startIcon={<DownloadIcon />} onClick={exportCsv} disabled={!data || !data.months_with_data}>Export CSV</Button>
        <Box sx={{ flexGrow: 1 }} />
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

export default UptimeAverageDialog;
